import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography, Card, CardContent, Avatar, Divider, InputAdornment, TableSortLabel
} from '@mui/material';
import { Edit, Delete, Add, Work, Search, Description, Schedule } from '@mui/icons-material';
import apiService from '../services/api';

interface Role {
  id: number;
  name: string;
  description: string;
  createdAt: string;
}

interface PaginatedRoles {
  roles: Role[];
  total: number;
}

const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'description' | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchRoles = async (pageNum = 0, pageSize = 10, searchTerm = '') => {
    try {
      // Temporarily disable search to test basic pagination
      const data: PaginatedRoles = await apiService.getRoles(pageNum + 1, pageSize);
      setRoles(data.roles || []);
      setTotal(data.total || 0);
    } catch (e) {
      setRoles([]);
      setTotal(0);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRoles(page, rowsPerPage, search);
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, search]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (role?: Role) => {
    setEditRole(role || null);
    setForm(role ? { name: role.name, description: role.description || '' } : { name: '', description: '' });
    setError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditRole(null);
    setForm({ name: '', description: '' });
    setError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Role name is required.');
      return;
    }
    setError(null);
    const token = localStorage.getItem('token');
    try {
      let res;
      if (editRole) {
        res = await apiService.updateRole(editRole.id.toString(), form);
      } else {
        res = await apiService.createRole(form);
      }
      if (res.error) {
        setError(res.message || 'Failed to save role.');
        return;
      }
      handleCloseDialog();
      fetchRoles(page, rowsPerPage, search);
    } catch {
      setError('Failed to save role.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await apiService.deleteRole(id.toString());
      fetchRoles(page, rowsPerPage, search);
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const handleSort = (column: 'name' | 'description') => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };



  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#1a237e', fontWeight: 600 }}>
        Roles Management
      </Typography>
      
      {/* Header with Actions and Search */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              Add Role
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by name or description..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#e9ecef',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                    }
                  }
                }}
                sx={{ 
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  }
                }}
              />
              
              <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                {roles.length} of {total} roles
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>

                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Work sx={{ fontSize: 18 }} />
                      <span>Name</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <TableSortLabel
                    active={sortBy === 'description'}
                    direction={sortBy === 'description' ? sortDirection : 'asc'}
                    onClick={() => handleSort('description')}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Description sx={{ fontSize: 18 }} />
                      <span>Description</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule sx={{ fontSize: 18 }} />
                    <span>Created At</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <span>Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow 
                  key={role.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f8f9fa',
                      transition: 'background-color 0.2s ease'
                    }
                  }}
                >

                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {role.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {role.description || (
                        <span style={{ fontStyle: 'italic', color: '#999' }}>
                          No description
                        </span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {role.createdAt ? new Date(role.createdAt).toLocaleDateString() : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <IconButton 
                        onClick={() => handleOpenDialog(role)}
                        sx={{ 
                          color: '#1976d2',
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                        }}
                        title="Edit Role"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(role.id)}
                        sx={{ 
                          color: '#d32f2f',
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                        }}
                        title="Delete Role"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
                             {roles.length === 0 && (
                 <TableRow>
                   <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                     <Typography variant="body1" sx={{ color: '#666' }}>
                       {search ? 'No roles found matching your criteria.' : 'No roles found.'}
                     </Typography>
                   </TableCell>
                 </TableRow>
               )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Divider />
        
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              color: '#666',
            }
          }}
        />
      </Card>

      {/* Dialog for Add/Edit Role */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1a237e', fontWeight: 600 }}>
          {editRole ? 'Edit Role' : 'Add Role'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            name="name"
            value={form.name}
            onChange={handleFormChange}
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            value={form.description}
            onChange={handleFormChange}
            fullWidth
            multiline
            minRows={3}
            placeholder="Enter role description..."
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles; 